import {useDiffEntries, useThreads} from '../stores/selectors'
import type {DiffEntry} from '@github-ui/diff-lines/types'
import {
  type Thread,
  type StaticDiffLine,
  type ThreadSubject,
  CommentStates,
} from '@github-ui/diff-lines/conversation-types'
import {useMemo} from 'react'
import type {PendingComment} from '../page-data/payloads/thread-previews'
import {useThreadPreviewPositioning} from '../page-data/loaders/use-thread-preview-positioning'

const DEFAULT_CONTEXT_LINES_TO_GRAB = 5

/**
 * Hook for getting pending review thread previews with positioning data stitched in
 * Returns PendingComment format with the full Thread object for each pending comment
 */
export function useGetPendingComments(pendingReviewIds?: number[]): PendingComment[] {
  const threads = useThreads(pendingReviewIds)
  const diffEntries = useDiffEntries()

  const {pendingComments, threadsWithoutContext} = useMemo(() => {
    if (!pendingReviewIds || pendingReviewIds.length === 0 || !threads) {
      return {pendingComments: [], threadsWithoutContext: []}
    }

    const comments: PendingComment[] = []
    const threadsWithoutContextInt: number[] = []

    for (const thread of threads) {
      const {diffSubject, hasContext} = buildDiffSubject(thread, diffEntries)

      if (!hasContext) {
        threadsWithoutContextInt.push(Number(thread.id))
      }

      // Get pending comments from this thread
      const threadPendingComments = (thread.commentsData?.comments ?? []).filter(
        comment => comment.state === CommentStates.Pending,
      )

      // Create a thread object with the built subject data and path
      const threadWithSubject: Thread = {
        ...thread,
        path: thread.positioning?.path || '',
        subject: diffSubject || thread.subject,
      }

      for (const currentComment of threadPendingComments) {
        if (!currentComment.databaseId) {
          continue
        }

        comments.push({
          commentID: currentComment.databaseId,
          thread: threadWithSubject,
        })
      }
    }

    return {pendingComments: comments, threadsWithoutContext: threadsWithoutContextInt}
  }, [diffEntries, threads, pendingReviewIds])

  const threadPreviewPositioningMap = useThreadPreviewPositioning(threadsWithoutContext).data

  const stitchedComments = useMemo(() => {
    if (threadsWithoutContext.length === 0 || !threadPreviewPositioningMap) {
      return pendingComments
    }

    // Create new objects instead of mutating to trigger React re-renders
    return pendingComments.map(pendingComment => {
      const threadId = Number(pendingComment.thread.id)

      const threadPreviewPositioning = threadPreviewPositioningMap[threadId]
      if (!threadPreviewPositioning) {
        return pendingComment
      }

      return {
        ...pendingComment,
        thread: {
          ...pendingComment.thread,
          path: threadPreviewPositioning.path,
          subject: threadPreviewPositioning,
        },
      }
    })
  }, [pendingComments, threadsWithoutContext, threadPreviewPositioningMap])

  return stitchedComments
}

/**
 * Builds the diff subject for a given thread and diff entries
 */
function buildDiffSubject(
  thread: Thread,
  diffEntries: Record<string, DiffEntry>,
): {
  diffSubject: ThreadSubject | undefined
  hasContext: boolean
} {
  let diffSubject = thread.subject
  let hasContext = true

  if (thread.positioning) {
    const diffEntry = diffEntries[thread.positioning.path]
    const diffLines = [] as StaticDiffLine[]

    if (diffEntry && !thread.isOutdated) {
      const endLineIndex = diffEntry.diffLines.findIndex(line => {
        return (
          (thread.positioning?.endDiffSide === 'RIGHT' && line.right === thread.positioning?.endLine) ||
          (thread.positioning?.endDiffSide === 'LEFT' && line.left === thread.positioning?.endLine)
        )
      })

      if (endLineIndex === -1) {
        hasContext = false
      } else {
        let startLineIndex = Math.max(0, endLineIndex - DEFAULT_CONTEXT_LINES_TO_GRAB)

        if (
          thread.positioning?.startLine &&
          thread.positioning?.startDiffSide &&
          (thread.positioning.startLine !== thread.positioning.endLine ||
            thread.positioning.startDiffSide !== thread.positioning.endDiffSide)
        ) {
          startLineIndex = diffEntry.diffLines.findIndex(line => {
            return (
              (thread.positioning?.startDiffSide === 'RIGHT' && line.right === thread.positioning?.startLine) ||
              (thread.positioning?.startDiffSide === 'LEFT' && line.left === thread.positioning?.startLine)
            )
          })
        }

        for (let i = endLineIndex; i >= startLineIndex; i--) {
          const diffLine = diffEntry.diffLines[i]

          if (diffLine) {
            diffLines.unshift(diffLine as StaticDiffLine)

            if (diffLine.type === 'HUNK') {
              // End the preview if we hit a hunk line
              break
            }
          }
        }
      }

      diffSubject = {...thread.subject, diffLines}
    }
  } else {
    hasContext = false
  }

  return {diffSubject, hasContext}
}
